/**
 * Review submission modal: open/close, star rating, REST submit.
 *
 * Expects `window.themeData` with `{ nonce, restUrl }` from wp_localize_script.
 *
 * @return {void}
 */
export function initReviewModal() {
	const modal = document.querySelector('[data-review-modal]');

	if (!modal) {
		return;
	}

	const dialog = modal.querySelector('[data-review-modal-dialog]');
	const form = modal.querySelector('[data-review-modal-form]');
	const messageEl = modal.querySelector('[data-review-modal-message]');
	const submitBtn = modal.querySelector('[data-review-modal-submit]');
	const ratingRoot = modal.querySelector('[data-review-rating]');
	const ratingInput = modal.querySelector('[data-review-rating-input]');
	const stars = ratingRoot
		? Array.from(ratingRoot.querySelectorAll('[data-review-star]'))
		: [];
	const openTriggers = document.querySelectorAll('.js-open-review-modal');
	const closeTriggers = modal.querySelectorAll('[data-review-modal-close]');

	let previouslyFocused = null;
	let closeTimer = null;

	const endpoint =
		window.themeData?.restUrl || '/wp-json/theme/v1/add-review';
	const nonce = window.themeData?.nonce || '';

	/**
	 * @param {number} value
	 * @param {'active'|'hover'} mode
	 * @return {void}
	 */
	const paintStars = (value, mode = 'active') => {
		const activeClass = 'active' === mode ? 'is-active' : 'is-hover';

		stars.forEach((star) => {
			const starValue = Number(star.dataset.value);
			star.classList.toggle(activeClass, starValue <= value);

			if ('active' === mode) {
				star.setAttribute('aria-checked', String(starValue === value));
			}
		});
	};

	/**
	 * @param {number} value
	 * @return {void}
	 */
	const setRating = (value) => {
		const next = Math.min(5, Math.max(1, value));

		if (ratingInput) {
			ratingInput.value = String(next);
		}

		stars.forEach((star) => star.classList.remove('is-hover'));
		paintStars(next, 'active');
	};

	/**
	 * @param {string} text
	 * @param {'success'|'error'|''} type
	 * @return {void}
	 */
	const setMessage = (text, type = '') => {
		if (!messageEl) {
			return;
		}

		messageEl.textContent = text;
		messageEl.hidden = !text;
		messageEl.classList.toggle('is-success', 'success' === type);
		messageEl.classList.toggle('is-error', 'error' === type);
	};

	/**
	 * @return {void}
	 */
	const openModal = () => {
		if (closeTimer) {
			window.clearTimeout(closeTimer);
			closeTimer = null;
		}

		previouslyFocused = document.activeElement;
		modal.hidden = false;
		modal.classList.add('is-open');
		modal.setAttribute('aria-hidden', 'false');
		document.body.classList.add('review-modal-open');
		setMessage('');

		window.requestAnimationFrame(() => {
			dialog?.focus();
		});
	};

	/**
	 * @return {void}
	 */
	const closeModal = () => {
		modal.classList.remove('is-open');
		modal.hidden = true;
		modal.setAttribute('aria-hidden', 'true');
		document.body.classList.remove('review-modal-open');

		if (closeTimer) {
			window.clearTimeout(closeTimer);
			closeTimer = null;
		}

		if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
			previouslyFocused.focus();
		}
	};

	/**
	 * @return {void}
	 */
	const resetForm = () => {
		form?.reset();
		setRating(5);
		setMessage('');

		if (submitBtn) {
			submitBtn.disabled = false;
		}
	};

	openTriggers.forEach((trigger) => {
		trigger.addEventListener('click', () => {
			resetForm();
			openModal();
		});
	});

	closeTriggers.forEach((trigger) => {
		trigger.addEventListener('click', () => {
			closeModal();
		});
	});

	document.addEventListener('keydown', (event) => {
		if ('Escape' !== event.key || modal.hidden) {
			return;
		}

		closeModal();
	});

	if (ratingRoot) {
		ratingRoot.addEventListener('mouseover', (event) => {
			const star = event.target.closest('[data-review-star]');

			if (!star || !ratingRoot.contains(star)) {
				return;
			}

			paintStars(Number(star.dataset.value), 'hover');
		});

		ratingRoot.addEventListener('mouseleave', () => {
			stars.forEach((star) => star.classList.remove('is-hover'));
			paintStars(Number(ratingInput?.value || 5), 'active');
		});

		ratingRoot.addEventListener('click', (event) => {
			const star = event.target.closest('[data-review-star]');

			if (!star || !ratingRoot.contains(star)) {
				return;
			}

			setRating(Number(star.dataset.value));
		});
	}

	form?.addEventListener('submit', async (event) => {
		event.preventDefault();
		setMessage('');

		const formData = new FormData(form);
		const authorName = String(formData.get('author_name') || '').trim();
		const content = String(formData.get('content') || '').trim();
		const rating = Number(formData.get('rating') || 5);
		const source = String(formData.get('source') || 'direct');

		if (!authorName || !content) {
			setMessage(
				form.dataset.requiredError || 'Please fill in the required fields.',
				'error'
			);
			return;
		}

		if (submitBtn) {
			submitBtn.disabled = true;
		}

		try {
			const response = await fetch(endpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-WP-Nonce': nonce,
				},
				body: JSON.stringify({
					author_name: authorName,
					content,
					rating,
					source,
				}),
			});

			let payload = null;

			try {
				payload = await response.json();
			} catch {
				payload = null;
			}

			if (!response.ok) {
				const errorMessage =
					payload?.message ||
					payload?.data?.message ||
					form.dataset.requestError ||
					'Something went wrong. Please try again later.';

				setMessage(errorMessage, 'error');

				if (submitBtn) {
					submitBtn.disabled = false;
				}

				return;
			}

			const successMessage =
				payload?.message ||
				'Thank you! Your review has been submitted for moderation.';

			setMessage(successMessage, 'success');

			closeTimer = window.setTimeout(() => {
				closeModal();
				resetForm();
			}, 2000);
		} catch {
			setMessage(
				form.dataset.networkError ||
					'Network error. Please check your connection and try again.',
				'error'
			);

			if (submitBtn) {
				submitBtn.disabled = false;
			}
		}
	});

	setRating(5);
}
